import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { first, isEmpty } from 'lodash-es';
import unset from 'lodash-es/unset';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ContactRepositoryPort } from 'contact/port/contact-repository.port';
import { JobOffer } from 'job/job-offer';
import { Module } from 'module/module';
import { TaskType } from 'task/model/task.type';
import { Task } from 'task/task';
import { UserRepositoryPort } from 'user/port/user-repository.port';

import { AppLocator } from '../../app.locator';

export interface TaskDocument {
	readonly id: string;
	readonly type: TaskType;
	readonly done: boolean;
	readonly ownerId: string;
	readonly dueDate: number;
	readonly modulesId: string[];
	readonly todoTitle: string;
	readonly contactId: string;
	readonly jobOfferId: string;
	readonly participantsId: string[];
	readonly todoDescription: string;
	readonly updateTimestamp: number;
	readonly externalEventId: string;
	readonly externalEventTitle: string;
}

export type TaskState = EntityState<TaskDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'tasks' })
export class TaskStore extends EntityStore<TaskState> {
	constructor() {
		super();
	}

	public get _contactRepositoryPort(): ContactRepositoryPort {
		return AppLocator.inject<ContactRepositoryPort>(ContactRepositoryPort);
	}

	public get _userRepositoryPort(): UserRepositoryPort {
		return AppLocator.inject<UserRepositoryPort>(UserRepositoryPort);
	}

	public asDocument(task: Task): TaskDocument {
		const document = task
			? ({
					id: task.id,
					type: task.type,
					done: task.done,
					ownerId: task.ownerId,
					todoTitle: task.todoTitle,
					contactId: task.contactId,
					jobOfferId: task.jobOfferId,
					dueDate: task.dueDate.getTime(),
					modulesId: task.modulesId || [],
					todoDescription: task.todoDescription,
					updateTimestamp: task.updateTimestamp,
					participantsId: task.participantsId || [],
					externalEventId: task.externalEventId,
					externalEventTitle: task.externalEventTitle,
			  } as TaskDocument)
			: null;

		if (isEmpty(document.modulesId)) {
			unset(document, 'modulesId');
		}

		return document;
	}

	public asEntity(
		document: TaskDocument,
		modules$: (ids: string[]) => Observable<Module[]>,
		jobOffers$: (ids: string[]) => Observable<JobOffer[]>
	): Task {
		const modulesId: string[] = document?.modulesId ?? [];

		return document
			? new Task(
					document.id,
					document.done,
					document.type,
					new Date(document.dueDate),
					document.ownerId,
					modulesId,
					document.todoTitle,
					document.contactId,
					document.jobOfferId,
					document.participantsId,
					document.todoDescription,
					document.updateTimestamp,
					document.externalEventId,
					document.externalEventTitle,
					!isEmpty(modulesId) ? modules$(modulesId) : of(null),
					document.contactId
						? this._contactRepositoryPort.observeContact(document.contactId)
						: of(null),
					document.participantsId
						? this._userRepositoryPort.observeUserByIds([
								...document.participantsId,
								document.ownerId,
						  ])
						: of([]),
					document.jobOfferId
						? jobOffers$([document.jobOfferId]).pipe(
								map((jobOffers) => first(jobOffers))
						  )
						: of(null)
			  )
			: null;
	}
}
